import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/feature/Layout';
import { supabase, Producto } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface CartItem {
  producto: Producto;
  cantidad: number;
}

export default function VentasPage() {
  const { user } = useAuth();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [montoRecibido, setMontoRecibido] = useState('');
  const [nombreFactura, setNombreFactura] = useState('');
  const [nit, setNit] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes] = await Promise.all([
          supabase.from('productos').select('*').order('nombre'),
        ]);
        if (prodRes.data) setProductos(prodRes.data);
      } catch {
        // error
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filtered = productos.filter((p) => {
    return (
      search === '' ||
      p.nombre.toLowerCase().includes(search.toLowerCase()) ||
      (p.marca && p.marca.toLowerCase().includes(search.toLowerCase())) ||
      (p.codigo_barras && p.codigo_barras.includes(search))
    );
  });

  const addToCart = (producto: Producto) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.producto.id === producto.id);
      if (existing) {
        if (existing.cantidad >= producto.stock) return prev;
        return prev.map((item) =>
          item.producto.id === producto.id
            ? { ...item, cantidad: item.cantidad + 1 }
            : item
        );
      }
      return [...prev, { producto, cantidad: 1 }];
    });
    showToast(`${producto.nombre} agregado al carrito`, 'success');
  };

  const removeFromCart = (productoId: string) => {
    setCart((prev) => prev.filter((item) => item.producto.id !== productoId));
  };

  const updateCartItem = (productoId: string, cantidad: number) => {
    if (cantidad < 1) {
      removeFromCart(productoId);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.producto.id === productoId
          ? { ...item, cantidad: Math.min(cantidad, item.producto.stock) }
          : item
      )
    );
  };

  const total = cart.reduce((sum, item) => sum + Number(item.producto.precio_venta) * item.cantidad, 0);
  const cambio = montoRecibido ? Math.max(0, Number(montoRecibido) - total) : 0;
  const puedeFinalizar =
    cart.length > 0 &&
    (metodoPago !== 'efectivo' || Number(montoRecibido) >= total);

  const handleCheckout = async () => {
    if (!user || saving) return;
    setSaving(true);

    try {
      // Registro atomico: venta + detalle + salida valorizada al CPP vigente
      const items = cart.map((item) => ({
        producto_id: item.producto.id,
        cantidad: item.cantidad,
        precio_unitario: Number(item.producto.precio_venta),
      }));

      const { error } = await supabase.rpc('cpp_registrar_venta', {
        p_items: items,
        p_metodo_pago: metodoPago,
        p_monto_recibido: metodoPago === 'efectivo' ? Number(montoRecibido) : total,
        p_cambio: metodoPago === 'efectivo' ? cambio : 0,
        p_nombre_factura: nombreFactura || '',
        p_nit: nit || '',
        p_comprobante_url: '',
        p_usuario_id: user.id,
      });

      if (error) throw error;

      setCart([]);
      setCheckoutOpen(false);
      setCartOpen(false);
      setMontoRecibido('');
      setNombreFactura('');
      setNit('');

      const { data: updated } = await supabase.from('productos').select('*').order('nombre');
      if (updated) setProductos(updated);

      showToast('Venta registrada exitosamente', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Error al registrar la venta', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner w-10 h-10 border-3 border-[#2563EB] border-t-transparent mx-auto mb-4"></div>
            <p className="text-sm text-gray-500">Cargando productos...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in space-y-6">
        {toast && (
          <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium animate-slide-in ${
            toast.type === 'success' ? 'bg-[#16A34A] text-white' : 'bg-[#DC2626] text-white'
          }`}>
            {toast.message}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#111827]">Ventas</h2>
            <p className="text-sm text-gray-500 mt-0.5">Registro de ventas</p>
          </div>
          <button
            onClick={() => setCartOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563EB] text-white rounded-lg text-sm font-medium hover:bg-[#1E40AF] transition-colors whitespace-nowrap cursor-pointer relative"
          >
            <i className="ri-shopping-cart-2-line w-4 h-4 flex items-center justify-center"></i>
            Carrito
            {cart.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#DC2626] text-white text-xs rounded-full flex items-center justify-center font-bold">
                {cart.reduce((s, i) => s + i.cantidad, 0)}
              </span>
            )}
          </button>
        </div>

        <div className="bg-white rounded-xl p-4">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, marca, código de barras..."
              className="w-full pl-10 pr-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((prod) => {
            const inCart = cart.find((item) => item.producto.id === prod.id);
            const sinStock = prod.stock === 0;

            return (
              <div
                key={prod.id}
                className="bg-white rounded-xl p-4 card-hover flex flex-col"
                data-product-shop
              >
                <div className="w-full h-32 bg-[#F4F6F8] rounded-lg flex items-center justify-center mb-3 overflow-hidden">
                  {prod.imagen_url ? (
                    <img src={prod.imagen_url} alt={prod.nombre} className="w-full h-full object-cover" />
                  ) : (
                    <i className="ri-box-3-line text-gray-400 text-3xl w-10 h-10 flex items-center justify-center"></i>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-400 mb-1">
                    {prod.tipo_producto || 'Sin tipo'}
                    {prod.marca ? ` · ${prod.marca}` : ''}
                  </p>
                  <h3 className="text-sm font-semibold text-[#111827] mb-2 line-clamp-2">{prod.nombre}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-[#111827]">Bs. {Number(prod.precio_venta).toLocaleString('es-BO')}</span>
                    <span className={`text-xs ${sinStock ? 'text-[#DC2626]' : 'text-gray-500'}`}>
                      {sinStock ? 'Sin Stock' : `Stock: ${prod.stock}`}
                    </span>
                  </div>
                </div>
                {inCart && (
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      onClick={() => updateCartItem(prod.id, inCart.cantidad - 1)}
                      className="w-7 h-7 flex items-center justify-center bg-[#F4F6F8] rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line w-3 h-3 flex items-center justify-center text-gray-600"></i>
                    </button>
                    <span className="text-sm font-semibold text-[#111827] min-w-[20px] text-center">{inCart.cantidad}</span>
                    <button
                      onClick={() => updateCartItem(prod.id, inCart.cantidad + 1)}
                      disabled={inCart.cantidad >= prod.stock}
                      className="w-7 h-7 flex items-center justify-center bg-[#F4F6F8] rounded-lg hover:bg-gray-200 transition-colors cursor-pointer disabled:opacity-30"
                    >
                      <i className="ri-add-line w-3 h-3 flex items-center justify-center text-gray-600"></i>
                    </button>
                  </div>
                )}
                <button
                  onClick={() => addToCart(prod)}
                  disabled={sinStock}
                  className={`mt-3 w-full py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sinStock
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-[#2563EB] text-white hover:bg-[#1E40AF]'
                  }`}
                >
                  {sinStock ? 'Sin Stock' : 'Agregar al Carrito'}
                </button>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12">
              <i className="ri-search-line text-gray-300 text-4xl w-10 h-10 flex items-center justify-center mx-auto mb-3"></i>
              <p className="text-sm text-gray-400">No se encontraron productos</p>
            </div>
          )}
        </div>

        {cartOpen && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setCartOpen(false)}></div>
            <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-50 flex flex-col shadow-2xl animate-slide-in-right">
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-[#111827]">Carrito de Ventas</h3>
                <button onClick={() => setCartOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F6F8] cursor-pointer">
                  <i className="ri-close-line text-gray-500 w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {cart.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-shopping-cart-2-line text-gray-300 text-4xl w-10 h-10 flex items-center justify-center mx-auto mb-3"></i>
                    <p className="text-sm text-gray-400">El carrito está vacío</p>
                  </div>
                ) : (
                  cart.map((item) => (
                    <div key={item.producto.id} className="flex items-center gap-3 p-3 bg-[#F4F6F8] rounded-xl">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {item.producto.imagen_url ? (
                          <img src={item.producto.imagen_url} alt={item.producto.nombre} className="w-12 h-12 object-cover" />
                        ) : (
                          <i className="ri-box-3-line text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#111827] truncate">{item.producto.nombre}</p>
                        <p className="text-xs text-gray-400">Bs. {Number(item.producto.precio_venta).toLocaleString('es-BO')} c/u</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => updateCartItem(item.producto.id, item.cantidad - 1)}
                          className="w-6 h-6 flex items-center justify-center bg-white rounded hover:bg-gray-100 cursor-pointer"
                        >
                          <i className="ri-subtract-line w-3 h-3 flex items-center justify-center text-gray-600"></i>
                        </button>
                        <span className="text-sm font-semibold w-6 text-center">{item.cantidad}</span>
                        <button
                          onClick={() => updateCartItem(item.producto.id, item.cantidad + 1)}
                          disabled={item.cantidad >= item.producto.stock}
                          className="w-6 h-6 flex items-center justify-center bg-white rounded hover:bg-gray-100 cursor-pointer disabled:opacity-30"
                        >
                          <i className="ri-add-line w-3 h-3 flex items-center justify-center text-gray-600"></i>
                        </button>
                      </div>
                      <p className="text-sm font-semibold text-[#111827] w-20 text-right whitespace-nowrap">
                        Bs. {(Number(item.producto.precio_venta) * item.cantidad).toLocaleString('es-BO')}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.producto.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-[#DC2626] cursor-pointer"
                      >
                        <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                      </button>
                    </div>
                  ))
                )}
              </div>

              {cart.length > 0 && (
                <div className="p-4 border-t border-gray-100 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">{cart.reduce((s, i) => s + i.cantidad, 0)} productos</span>
                    <span className="text-xl font-bold text-[#111827]">Bs. {total.toLocaleString('es-BO')}</span>
                  </div>
                  <button
                    onClick={() => setCheckoutOpen(true)}
                    className="w-full py-3 bg-[#16A34A] hover:bg-[#15803D] text-white font-medium rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Finalizar Venta
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {checkoutOpen && (
          <>
            <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setCheckoutOpen(false)}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
                <div className="p-6 space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-[#111827]">Finalizar Venta</h3>
                    <button onClick={() => setCheckoutOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F6F8] cursor-pointer">
                      <i className="ri-close-line text-gray-500 w-5 h-5 flex items-center justify-center"></i>
                    </button>
                  </div>

                  <div className="bg-[#F4F6F8] rounded-xl p-4">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-500">Total a pagar</span>
                      <span className="text-2xl font-bold text-[#111827]">Bs. {total.toLocaleString('es-BO')}</span>
                    </div>
                    <p className="text-xs text-gray-400">{cart.reduce((s, i) => s + i.cantidad, 0)} productos en el carrito</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pago</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['efectivo', 'qr'].map((metodo) => (
                        <button
                          key={metodo}
                          onClick={() => setMetodoPago(metodo)}
                          className={`py-2.5 px-3 rounded-lg text-sm font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                            metodoPago === metodo
                              ? 'bg-[#2563EB] text-white'
                              : 'bg-[#F4F6F8] text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {metodo}
                        </button>
                      ))}
                    </div>
                  </div>

                  {metodoPago === 'efectivo' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Monto Recibido</label>
                      <input
                        type="number"
                        value={montoRecibido}
                        onChange={(e) => setMontoRecibido(e.target.value)}
                        className="w-full px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                        placeholder="Ingrese el monto recibido"
                      />
                      {Number(montoRecibido) > 0 && (
                        <p className={`text-sm mt-2 ${cambio >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
                          Cambio: Bs. {cambio.toLocaleString('es-BO')}
                          {Number(montoRecibido) < total && ' (Monto insuficiente)'}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Facturación (opcional)</p>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={nombreFactura}
                        onChange={(e) => setNombreFactura(e.target.value)}
                        className="w-full px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                        placeholder="Nombre / Razón Social"
                      />
                      <input
                        type="text"
                        value={nit}
                        onChange={(e) => setNit(e.target.value)}
                        className="w-full px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                        placeholder="NIT / CI"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setCheckoutOpen(false)}
                      className="flex-1 py-2.5 bg-[#F4F6F8] text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleCheckout}
                      disabled={!puedeFinalizar || saving}
                      className="flex-1 py-2.5 bg-[#16A34A] text-white rounded-lg text-sm font-medium hover:bg-[#15803D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <>
                          <div className="spinner w-4 h-4 border-2 border-white border-t-transparent"></div>
                          <span>Procesando...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                          <span>Confirmar Venta</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}